import * as React from 'react';
import * as styled from './styled';
import {useRoute} from 'react-router5';
import {Group, GroupObject, GroupProps} from './components/Group';
import {Field, FieldObject} from './components/Field';
import {Button, ButtonVariant} from './components/Button';
import {FilterProps} from './types';

/**
 * The filter container.
 * @param {FilterProps} props
 * @return {React.ReactElement<FilterProps>}
 * @constructor
 */
function Filter(props: FilterProps): React.ReactElement<FilterProps> {
  const {route, router} = useRoute();

  /**
   * Get groups.
   * @return {React.ReactElement<GroupProps>[]}
   */
  function getGroups() {
    return Array.isArray(props.children) ? (
      props.children
    ) : [props.children];
  }

  /**
   * Get a group.
   * @param {string} name
   * @return {React.ReactElement<GroupProps> | undefined}
   */
  function getGroup(name: string) {
    return getGroups().find((group) => (
      group.props.name === name
    ));
  }

  /**
   * Get all group name's.
   * @return {GroupObject[]}
   */
  function getNames(): GroupObject['name'][] {
    return getGroups().map((group) => (
      group.props.name
    ));
  }

  /**
   * Get the params.
   * @return {[string, any][]}
   */
  function getParams() {
    return Object.entries(route?.params).filter((param) => (
      getNames().find((name) => (
        name === param[0]
      ))
    ));
  }

  /**
   * Get value label.
   * @param {string} name
   * @param {string} value
   * @return {(string | undefined)[]}
   */
  function getValueLabel(name: string, value: string) {
    const group = getGroup(name);
    const children = group?.props.children({
      name: group?.props.name,
      label: group?.props.label,
      variant: group?.props.variant,
    });

    return Array.isArray(children) ? (
      children.find((field) => (
        field.props.value === value
      ))?.props.label
    ) : children?.props.label;
  }

  /**
   * Render content.
   * @return {JSX.Element}
   */
  function renderContent(): JSX.Element {
    return (
      <styled.Container>
        {props.children}
      </styled.Container>
    );
  }

  /**
   * Render controls.
   * @return {JSX.Element | undefined}
   */
  function renderControls(): JSX.Element | undefined {
    /**
     * Render params.
     * @return {JSX.Element}
     */
    function renderParams(): JSX.Element {
      /**
       * Render label.
       * @param {string} name
       * @return {JSX.Element}
       */
      function renderLabel(name: string): JSX.Element {
        return (
          <span>
            {getGroup(name)?.props.label}:
          </span>
        );
      }

      /**
       * Render value.
       * @param {string} name
       * @param {string | string[]} value
       * @return {JSX.Element}
       */
      function renderValue(
          name: string,
          value: string | string[],
      ): JSX.Element {
        return Array.isArray(value) ? (
          <React.Fragment>
            {value.map((item) => (
              <span>{getValueLabel(name, item)}</span>
            ))}
          </React.Fragment>
        ) : (
          <span>{getValueLabel(name, value)}</span>
        );
      }

      /**
       * The delete event handler.
       * @param {string} param
       */
      function handleDelete(param: string): void {
        router.navigate(route.name, (
          Object.fromEntries((
            Object.entries(route.params).filter((value) => (
              value[0] !== param
            ))
          ))
        ));
      }

      return (
        <styled.Container>
          <styled.Params>
            {getParams().map((param) => (
              <styled.Param onClick={() => handleDelete(param[0])}>
                {renderLabel(param[0])}
                {renderValue(param[0], param[1])}
              </styled.Param>
            ))}
          </styled.Params>
        </styled.Container>
      );
    }

    /**
     * Render reset control.
     * @return {JSX.Element | undefined}
     */
    function renderReset(): JSX.Element | undefined {
      /**
       * The click event handler.
       */
      function handleClick(): void {
        router.navigate(route.name, {});
      }

      return getParams().length > 0 ? (
        <Button variant={ButtonVariant.Outline} onClick={handleClick}>
          Сбросить все фильтры
        </Button>
      ) : undefined;
    }

    return getParams().length > 0 ? (
      <styled.Content>
        <styled.Container>
          {renderParams()}
          {renderReset()}
        </styled.Container>
      </styled.Content>
    ) : undefined;
  }

  return (
    <styled.Wrapper>
      {renderContent()}
      {renderControls()}
    </styled.Wrapper>
  );
}

Filter.Group = Group;
Filter.Field = Field;

export default Filter;
